import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Divider,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  getCountryOptions,
  getPostalCodeLabel,
  getStateOptions,
  hasPresetStates,
  normalizeCountry,
} from '../utils/locations';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  ArrowBack as ArrowBackIcon,
  Assignment as CaseIcon,
  Folder as ProjectIcon,
  Add as AddIcon,
  AccountBalanceWallet as WalletIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Gavel as LegalIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Handshake as VendorIcon,
} from '@mui/icons-material';
import { projectAPI, firmAPI, caseAPI, userAPI } from '../services/api';
import { firmService, Firm, OperationalManager } from '../services/firmService';
import { caseService } from '../services/caseService';
import { useAuth } from '../contexts/AuthContext';
import { canViewClientWallet, userCanAccessFirm } from '../utils/firmAccess';

interface Case {
  id: number;
  title: string;
  project_id: number;
  project?: { name: string };
  assigned_to?: { name: string };
  creator?: { name: string };
  status: string;
  file_number?: string;
  client_reference_id?: string | null;
  created_at: string;
}

interface OperationalManagerOption {
  id: number;
  name: string;
  email: string;
}

interface BillingSummary {
  period_start: string;
  period_end: string;
  currency: string;
  estimate_count: number;
  total_estimates_amount: number;
  timer_entry_count: number;
  total_timer_amount: number;
  total_amount: number;
}

const openInNewTab = (path: string) => {
  window.open(path, '_blank', 'noopener,noreferrer');
};

const getCategoryLabel = (category: string) => {
  switch (category) {
    case 'prepaid': return 'Prepaid';
    case 'postpaid': return 'Postpaid';
    case 'trial': return 'Trial';
    case 'suspended': return 'Suspended';
    default: return category;
  }
};

const getCategoryColor = (category: string): 'info' | 'default' | 'warning' | 'error' => {
  switch (category) {
    case 'prepaid': return 'info';
    case 'postpaid': return 'default';
    case 'trial': return 'warning';
    case 'suspended': return 'error';
    default: return 'default';
  }
};

const formatMonthYear = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const FirmDashboard: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isPmpAdmin = () =>
    user?.role === 'pmp_admin' || (user?.roles?.some((role) => role.slug === 'pmp_admin') ?? false);
  const isSiteAdmin = () =>
    user?.role === 'site_admin' || (user?.roles?.some((role) => role.slug === 'site_admin') ?? false);
  const isPmpAdminOrSiteAdmin = () => isPmpAdmin() || isSiteAdmin();
  const hasClientManagerRole = () =>
    (user as any)?.roles?.some((r: any) => r.slug === 'client_manager') ?? false;
  const isClientAdmin = () =>
    user?.roles?.some((role) =>
      ['firm_admin', 'firm_main', 'client_admin', 'client_manager'].includes(role.slug)
    ) ?? false;
  const canAccessClientEstimates = () => {
    if (user?.is_finance_user) return true;
    return user?.roles?.some((role) =>
      ['firm_admin', 'firm_main', 'client_admin', 'client_manager', 'client_finance_user'].includes(role.slug)
    ) ?? false;
  };
  const canViewMembersAndLegal = () => {
    if (isPmpAdminOrSiteAdmin() || isClientAdmin()) return true;
    const blocked = ['client_user', 'client_finance_user', 'client_supervisor'];
    return !(user?.roles?.some((role) => blocked.includes(role.slug)) ?? false);
  };

  const [firm, setFirm] = useState<Firm | null>(null);
  const [operationalManagers, setOperationalManagers] = useState<OperationalManager[]>([]);
  const [defaultOperationalManagers, setDefaultOperationalManagers] = useState<OperationalManager[]>([]);
  const [recentCases, setRecentCases] = useState<Case[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [walletBalance, setWalletBalance] = useState<any>(null);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [editAddressOpen, setEditAddressOpen] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [editManagerOpen, setEditManagerOpen] = useState(false);
  const [savingManager, setSavingManager] = useState(false);
  const [managerCandidates, setManagerCandidates] = useState<OperationalManagerOption[]>([]);
  const [selectedManagers, setSelectedManagers] = useState<OperationalManagerOption[]>([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [addressForm, setAddressForm] = useState({
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    contact_phone: '',
    website: '',
  });

  const showFileNumberColumn = useMemo(
    () => recentCases.some((c) => c.file_number && c.file_number.trim() !== ''),
    [recentCases]
  );

  const showClientIdColumn = useMemo(
    () => recentCases.some((c) => c.client_reference_id && c.client_reference_id.trim() !== ''),
    [recentCases]
  );

  useEffect(() => {
    if (firmId) {
      fetchFirmData();
      fetchFirmProjects();
      fetchRecentCases();
    }
  }, [firmId]);

  const fetchFirmData = async () => {
    try {
      const response = await firmService.getFirmById(parseInt(firmId!));
      setFirm(response.data);
      setOperationalManagers(response.data.operational_managers || []);
      setDefaultOperationalManagers(response.default_operational_managers || []);
      if (response.data.type === 'client' && canViewClientWallet(user, firmId)) {
        fetchWalletBalance();
        fetchBillingSummary();
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const fetchBillingSummary = async () => {
    try {
      const response = await firmAPI.getBillingSummary(parseInt(firmId!));
      if (response.data.success) {
        setBillingSummary(response.data.data);
      }
    } catch {
      setBillingSummary(null);
    }
  };

  const fetchRecentCases = async () => {
    try {
      const response = await caseService.getFirmCases(parseInt(firmId!));
      setRecentCases(response.data.data.slice(0, 10));
    } catch {
      setRecentCases([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFirmProjects = async () => {
    try {
      const res = await projectAPI.getAll({ firm_id: firmId });
      setProjects(res.data.data.data || res.data.data || []);
    } catch {
      setProjects([]);
    }
  };

  const fetchWalletBalance = async () => {
    if (!firmId) return;
    try {
      const response = await firmAPI.getWalletBalance(parseInt(firmId!));
      if (response.data.success) {
        setWalletBalance(response.data.data);
      }
    } catch {
      setWalletBalance(null);
    }
  };

  const handleExportCases = async () => {
    setExporting(true);
    try {
      const response = await caseAPI.exportExcel({ firm_id: firmId });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${firm?.name?.replace(/\s+/g, '_') || 'firm'}_cases_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export cases');
    } finally {
      setExporting(false);
    }
  };

  const openEditAddress = () => {
    if (!firm) return;
    setAddressForm({
      address_1: firm.address_1 || '',
      address_2: firm.address_2 || '',
      city: firm.city || '',
      state: firm.state || '',
      zip_code: firm.zip_code || '',
      country: normalizeCountry(firm.country) || firm.country || '',
      contact_phone: firm.contact_phone || '',
      website: firm.website || '',
    });
    setEditAddressOpen(true);
  };

  const handleSaveAddress = async () => {
    try {
      setSavingAddress(true);
      await firmService.updateFirm(parseInt(firmId!), addressForm);
      setEditAddressOpen(false);
      await fetchFirmData();
    } catch (err: any) {
      setError(err.message || 'Failed to update address');
    } finally {
      setSavingAddress(false);
    }
  };

  const openEditManager = async () => {
    setSelectedManagers(operationalManagers);
    setEditManagerOpen(true);
    setLoadingManagers(true);
    try {
      const res = await userAPI.list({
        status: 'active',
        roles: ['pmp_admin', 'site_admin', 'client_manager', 'internal_manager', 'internal_staff'],
        limit: 200,
      });
      const list = (res.data.data || []) as OperationalManagerOption[];
      const extras = operationalManagers.filter((m) => !list.some((u) => u.id === m.id));
      setManagerCandidates([...extras, ...list]);
    } catch {
      setManagerCandidates(operationalManagers);
    } finally {
      setLoadingManagers(false);
    }
  };

  const handleSaveManager = async () => {
    try {
      setSavingManager(true);
      await firmService.updateOperationalManager(parseInt(firmId!), selectedManagers.map((m) => m.id));
      setEditManagerOpen(false);
      setSuccessMessage('Operational managers updated');
      await fetchFirmData();
    } catch (err: any) {
      setError(err.message || 'Failed to update operational managers');
    } finally {
      setSavingManager(false);
    }
  };

  const getStatusChip = (status: string) => {
    const colors: Record<string, 'primary' | 'warning' | 'success' | 'secondary' | 'error' | 'info' | 'default'> = {
      open: 'primary', in_progress: 'warning', completed: 'success', on_hold: 'secondary',
      cancelled: 'error', closed: 'default', pending: 'warning',
    };
    const label = status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return <Chip label={label} color={colors[status.toLowerCase()] || 'default'} size="small" />;
  };

  const getTypeChip = (type: string) =>
    type === 'client'
      ? <Chip label="Client" color="primary" size="small" variant="outlined" />
      : <Chip label="Vendor" color="secondary" size="small" variant="outlined" />;

  const billingIsEmpty = billingSummary
    && billingSummary.total_amount === 0
    && billingSummary.estimate_count === 0
    && billingSummary.timer_entry_count === 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !firm) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  if (!firm) {
    return <Alert severity="warning" sx={{ mt: 2 }}>Firm not found</Alert>;
  }

  const isClient = firm.type === 'client';
  const firmManagerIds = new Set(operationalManagers.map((m) => m.id));
  const globalManagerNames = defaultOperationalManagers
    .filter((m) => !firmManagerIds.has(m.id))
    .map((m) => m.name);
  const assignedManagerLabel = operationalManagers.length > 0
    ? operationalManagers.map((m) => m.name).join(', ')
    : defaultOperationalManagers.length > 0
      ? `Not assigned (emails go to ${defaultOperationalManagers.map((m) => m.name).join(', ')})`
      : 'Not assigned';

  return (
    <Box sx={{ pb: 4 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => { e.preventDefault(); navigate('/firms'); }}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <ArrowBackIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Back to Firms
        </Link>
        <Typography color="text.primary" variant="body2">{firm.name}</Typography>
      </Breadcrumbs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Page header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {firm.name}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
          {getTypeChip(firm.type)}
          {firm.status && (
            <Chip
              label={firm.status.charAt(0).toUpperCase() + firm.status.slice(1)}
              color={firm.status === 'approved' ? 'success' : firm.status === 'pending' ? 'warning' : 'error'}
              size="small"
            />
          )}
          <Chip
            label={firm.category ? getCategoryLabel(firm.category) : 'Billing type not set'}
            color={firm.category ? getCategoryColor(firm.category) : 'default'}
            size="small"
            variant={firm.category ? 'filled' : 'outlined'}
          />
          {firm.type === 'client' && firm.access_mode && firm.access_mode !== 'full' && (
            <Chip
              size="small"
              color="error"
              label={
                firm.access_mode === 'download_only' && firm.download_access_until && new Date(firm.download_access_until) > new Date()
                  ? `Download only until ${new Date(firm.download_access_until).toLocaleString()}`
                  : 'Sign-in suspended'
              }
            />
          )}
        </Box>
        {isClient && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Operational Managers:{' '}
              <Typography
                component="span"
                variant="body2"
                color={operationalManagers.length ? 'text.primary' : 'text.disabled'}
                fontWeight={operationalManagers.length ? 500 : 400}
              >
                {assignedManagerLabel}
              </Typography>
            </Typography>
            {isPmpAdminOrSiteAdmin() && (
              <IconButton size="small" onClick={openEditManager} aria-label="Edit operational managers">
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {operationalManagers.length > 0 && globalManagerNames.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }}>
              Also BCC: {globalManagerNames.join(', ')}
            </Typography>
          )}
        </Box>
        )}
      </Box>

      {/* Quick actions — sticky */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          px: 2,
          py: 1.5,
          mb: 3,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openInNewTab(`/firms/${firmId}/cases/new`)}>
          New Case
        </Button>
        <Button variant="outlined" size="small" onClick={() => openInNewTab(`/firms/${firmId}/cases/bulk`)}>
          Create cases
        </Button>
        {isClient && (isPmpAdmin() || (canAccessClientEstimates() && userCanAccessFirm(user, firmId))) && (
          <Button variant="outlined" size="small" color="success" onClick={() => openInNewTab(`/firms/${firmId}/client-estimates`)}>
            Pending Estimates
          </Button>
        )}
        {isClient && canViewClientWallet(user, firmId) && (
          <>
            <Button variant="outlined" size="small" startIcon={<WalletIcon />} onClick={() => openInNewTab(`/firms/${firmId}/wallet`)}>
              Wallet
            </Button>
            <Button variant="outlined" size="small" startIcon={<SettingsIcon />} onClick={() => openInNewTab(`/firms/${firmId}/autopay`)}>
              Auto-Pay
            </Button>
          </>
        )}
        {canViewMembersAndLegal() && (
        <Button variant="outlined" size="small" startIcon={<GroupIcon />} onClick={() => openInNewTab(`/firms/${firmId}/members`)}>
          Members
        </Button>
        )}
        {(canViewMembersAndLegal() || isPmpAdminOrSiteAdmin() || (hasClientManagerRole() && isClient)) && (
          <>
        <Button
          variant="outlined"
          size="small"
          endIcon={<MoreVertIcon />}
          onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
        >
          More
        </Button>
        <Menu anchorEl={moreMenuAnchor} open={Boolean(moreMenuAnchor)} onClose={() => setMoreMenuAnchor(null)}>
          {canViewMembersAndLegal() && (
          <MenuItem onClick={() => { setMoreMenuAnchor(null); openInNewTab(`/firms/${firmId}/legal`); }}>
            <ListItemIcon><LegalIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Legal</ListItemText>
          </MenuItem>
          )}
          {isClient && isPmpAdminOrSiteAdmin() && (
            <MenuItem onClick={() => { setMoreMenuAnchor(null); openInNewTab(`/firms/${firmId}/settings`); }}>
              <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
          )}
          {(isPmpAdminOrSiteAdmin() || hasClientManagerRole()) && isClient && (
            <MenuItem onClick={() => { setMoreMenuAnchor(null); openInNewTab(`/firms/${firmId}/vendor-assign`); }}>
              <ListItemIcon><VendorIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Vendor Assignment</ListItemText>
            </MenuItem>
          )}
          {isPmpAdminOrSiteAdmin() && (
            <MenuItem onClick={() => { setMoreMenuAnchor(null); openInNewTab(`/firms/${firmId}/projects/new`); }}>
              <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Create Project</ListItemText>
            </MenuItem>
          )}
        </Menu>
          </>
        )}
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Summary row: Wallet + Billing side by side */}
        {isClient && canViewClientWallet(user, firmId) && (walletBalance || billingSummary) && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: walletBalance && billingSummary ? '1fr 1fr' : '1fr' },
              gap: 2,
            }}
          >
            {walletBalance && (
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WalletIcon fontSize="small" color="primary" /> Wallet Balance
                    </Typography>
                    <Button size="small" variant="text" onClick={() => openInNewTab(`/firms/${firmId}/wallet`)}>
                      View Details →
                    </Button>
                  </Box>
                  <Typography variant="h4" color="primary.main" fontWeight={700} lineHeight={1.2}>
                    {walletBalance.currency} {parseFloat(walletBalance.balance).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {firm.category ? `${getCategoryLabel(firm.category)} client account` : 'Client account'}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {billingSummary && (
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptIcon fontSize="small" color="primary" />
                      {formatMonthYear(billingSummary.period_start)}
                      <Chip label="Live" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                    </Typography>
                    <Typography variant="h5" color="primary.main" fontWeight={700}>
                      {billingSummary.currency} {billingSummary.total_amount.toFixed(2)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: billingIsEmpty ? 1 : 0 }}>
                    Estimates: {billingSummary.estimate_count} · {billingSummary.currency} {billingSummary.total_estimates_amount.toFixed(2)}
                    {'  |  '}
                    Time: {billingSummary.timer_entry_count} entries · {billingSummary.currency} {billingSummary.total_timer_amount.toFixed(2)}
                  </Typography>
                  {billingIsEmpty && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      No unbilled estimates or timer entries this month.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {/* Organization Details — 2 columns */}
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon fontSize="small" color="primary" /> Organization Details
              </Typography>
              {(isPmpAdmin() || isClientAdmin()) && (
                <IconButton size="small" onClick={openEditAddress} title="Edit organization details">
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 3,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Name
                </Typography>
                <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{firm.name}</Typography>

                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} sx={{ mt: 2, display: 'block' }}>
                  Address
                </Typography>
                <Box sx={{ mt: 0.5, display: 'flex', gap: 0.75 }}>
                  <LocationIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2">{firm.address_1}</Typography>
                    {firm.address_2 && <Typography variant="body2">{firm.address_2}</Typography>}
                    <Typography variant="body2">{firm.city}, {firm.state} {firm.zip_code}</Typography>
                    <Typography variant="body2">{firm.country}</Typography>
                  </Box>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Contact
                </Typography>
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2">{firm.contact_phone || '—'}</Typography>
                </Box>

                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} sx={{ mt: 2, display: 'block' }}>
                  Website
                </Typography>
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <WebsiteIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  {firm.website ? (
                    <Link href={firm.website} target="_blank" rel="noopener noreferrer" variant="body2">
                      {firm.website.replace(/^https?:\/\//, '')}
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Projects */}
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ProjectIcon fontSize="small" /> Projects
                <Chip label={projects.length} size="small" sx={{ height: 20, minWidth: 24 }} />
              </Typography>
              <Button size="small" variant="text" onClick={() => openInNewTab(`/firms/${firmId}/projects`)}>
                View All →
              </Button>
            </Box>
            <Divider sx={{ mb: 0 }} />
            {projects.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No projects for this firm
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={60}>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell width={80} align="center">Cases</TableCell>
                      <TableCell width={100}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.slice(0, 5).map((p) => (
                      <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => openInNewTab(`/firms/${firmId}/projects/${p.id}`)}>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">#{p.id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="primary.main" fontWeight={500}>
                            {p.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={p.cases_count ?? 0} size="small" variant="outlined" sx={{ minWidth: 36, height: 22 }} />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={String(p.status).replace('_', ' ')}
                            size="small"
                            color={p.status === 'active' ? 'primary' : 'default'}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Recent Cases */}
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CaseIcon fontSize="small" color="secondary" /> Recent Cases
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
                  onClick={handleExportCases}
                  disabled={exporting}
                >
                  Export
                </Button>
                <Button variant="outlined" size="small" onClick={() => openInNewTab(`/firms/${firmId}/cases`)}>
                  View All
                </Button>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openInNewTab(`/firms/${firmId}/cases/new`)}>
                  New Case
                </Button>
                <Button variant="outlined" size="small" onClick={() => openInNewTab(`/firms/${firmId}/cases/bulk`)}>
                  Create cases
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 0 }} />
            {recentCases.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">No cases found for this firm</Typography>
                <Button size="small" sx={{ mt: 1 }} startIcon={<AddIcon />} onClick={() => openInNewTab(`/firms/${firmId}/cases/new`)}>
                  Create first case
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={70}>ID</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Project</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created By</TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Assigned To</TableCell>
                      <TableCell width={110}>Status</TableCell>
                      {showClientIdColumn && (
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Client ID</TableCell>
                      )}
                      {showFileNumberColumn && (
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>File #</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentCases.map((caseItem) => (
                      <TableRow
                        key={caseItem.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openInNewTab(`/firms/${firmId}/cases/${caseItem.id}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>#{caseItem.id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="primary.main" fontWeight={500}>
                            {caseItem.title}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{caseItem.project?.name || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography variant="body2">{caseItem.creator?.name || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="body2" color={caseItem.assigned_to?.name ? 'text.primary' : 'text.disabled'}>
                            {caseItem.assigned_to?.name || 'Unassigned'}
                          </Typography>
                        </TableCell>
                        <TableCell>{getStatusChip(caseItem.status)}</TableCell>
                        {showClientIdColumn && (
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                            <Typography variant="body2">{caseItem.client_reference_id || '—'}</Typography>
                          </TableCell>
                        )}
                        {showFileNumberColumn && (
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                            <Typography variant="body2">{caseItem.file_number || '—'}</Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Edit Address Dialog */}
      <Dialog open={editAddressOpen} onClose={() => !savingAddress && setEditAddressOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Organization Details</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Address Line 1" value={addressForm.address_1} onChange={(e) => setAddressForm({ ...addressForm, address_1: e.target.value })} sx={{ mt: 2 }} required />
          <TextField fullWidth label="Address Line 2" value={addressForm.address_2} onChange={(e) => setAddressForm({ ...addressForm, address_2: e.target.value })} sx={{ mt: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              select
              label="Country"
              value={addressForm.country}
              onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value, state: '' })}
            >
              {getCountryOptions(addressForm.country).map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </TextField>
            {hasPresetStates(addressForm.country) ? (
              <TextField
                fullWidth
                select
                label="State"
                value={addressForm.state}
                onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                required
              >
                <MenuItem value="">
                  <em>- Select -</em>
                </MenuItem>
                {getStateOptions(addressForm.country, addressForm.state).map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField fullWidth label="State" value={addressForm.state} onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })} required />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField fullWidth label="City" value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} required />
            <TextField fullWidth label={getPostalCodeLabel(addressForm.country)} value={addressForm.zip_code} onChange={(e) => setAddressForm({ ...addressForm, zip_code: e.target.value })} required />
          </Box>
          <TextField fullWidth label="Contact Phone" value={addressForm.contact_phone} onChange={(e) => setAddressForm({ ...addressForm, contact_phone: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Website" value={addressForm.website} onChange={(e) => setAddressForm({ ...addressForm, website: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAddressOpen(false)} disabled={savingAddress}>Cancel</Button>
          <Button onClick={handleSaveAddress} variant="contained" disabled={savingAddress}>
            {savingAddress ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editManagerOpen} onClose={() => !savingManager && setEditManagerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Operational Managers</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            These people are emailed (To) when a client user creates a case. People set as Operational Managers in Email Settings are always BCC’d so nobody is missed.
          </Typography>
          <Autocomplete
            multiple
            options={managerCandidates}
            loading={loadingManagers}
            value={selectedManagers}
            onChange={(_e, val) => setSelectedManagers(val)}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Operational Managers" placeholder="Add people" />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditManagerOpen(false)} disabled={savingManager}>Cancel</Button>
          <Button onClick={() => setSelectedManagers([])} disabled={savingManager || selectedManagers.length === 0}>
            Clear
          </Button>
          <Button onClick={handleSaveManager} variant="contained" disabled={savingManager}>
            {savingManager ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FirmDashboard;
